import { useRef, useState, useEffect, useContext, useMemo } from "react";

//Components
import {
  TextField,
  Box,
  Button,
  InputAdornment,
  FormHelperText,
} from "@material-ui/core";
import CheckboxWithLabel from "../CheckboxWithLabel";
import SingleDropDown from "../SingleDropDown";

//Contexts
import { SetupContext } from "../../Contexts/SetupContext";
import { INCIDENT_ACTIONS } from "../../Actions/IncidentActions";
import { IncidentsContext } from "../../Contexts/IncidentsContext";
import { UserContext } from "../../Contexts/UserContext";
import { MutationContext } from "../../Contexts/MutationContext";

//Fetching
import { useMutation, useApolloClient } from "@apollo/client";
import { ADD_OR_UPDATE_VEHICLE_IN_INCIDENT } from "../../Mutations/IncidentMutations/UpdateVehicleInIncident";
import CircularProgress from "@material-ui/core/CircularProgress";

//Styling
import { useStyles } from "../../Styles/styles";

//Utils
import PropTypes from "prop-types";
import VehicleAutoSuggest from "../VehicleAutoSuggest";
import AlertComponent from "../AlertComponent";
import { createEmptyIncident, createEmptyVis } from "../../utils/EmptyObjects";
import { shouldBeLocked } from "../../utils/Roles";
import { VEHICLE_ACTIONS } from "../../Actions/VehicleActions";
import VehicleHistoryModal from "../VehicleHistoryModal";
import TwentyEightButton from "../TwentyEightButton";
import { isVisEmpty, localGetData, wordSort } from "../../utils/utils";
import { getValueFromEventValue } from "../../utils/getValueFromEventValue";
import clsx from "clsx";
import CustomAutoComplete from "../CustomAutoComplete";
import _ from "lodash";
import { DashboardContext } from "../../Contexts/DashboardContext";
import { useHandleRemoveVehicle } from "./hooks";

import moment from "moment";

const VehicleDetail = ({
  showVehicleSearch,
  setShowVehicleSearch,
  vis = {
    id: "",
    abandoned: false,
    impounded: false,
    operator: {
      id: "",
      personalAttribute: {
        id: "",
        firstName: "",
        lastName: "",
      },
    },
    owner: {
      id: "",
      personalAttribute: {
        id: "",
        firstName: "",
        lastName: "",
      },
    },
    reasonSeized: {
      id: "",
      name: "",
    },
    suspectDriven: false,
    wrecker: {
      id: "",
      name: "",
    },
    towingDestination: {
      id: "",
      name: "",
    },
    usedInCrime: false,
    vehicle: {
      id: "",
      description: "",
      plateType: {
        id: "",
        name: "",
      },
      value: "",
      vehicleColor: {
        id: "",
        name: "",
      },
      vehicleMake: {
        id: "",
        name: "",
      },
      vehicleModel: {
        id: "",
        name: "",
      },
      vehiclePlate: {
        id: "",
        name: "",
      },
      vehicleState: {
        id: "",
        name: "",
      },
      vehicleVin: {
        id: "",
        name: "",
      },
      year: "",
    },
    vehicleOfInterest: false,
    vehicleRole: {
      id: "",
      name: "",
    },
  },
  visIndex,
  incidentId,
  incidentUuid,
  incidentIndex,
  piss = [],
  vissLength,
  vehicleTab,
  setVehicleTab,
  incidentOriId,
  disabled = undefined,
  userRoles,
  inIncident = true,
  createNewIncident,
  setPopper,
  ...props
}) => {
  const memoizedLocked = useMemo(
    () => shouldBeLocked(disabled, props?.canEdit, userRoles),
    [disabled, props?.canEdit, userRoles]
  );
  const classes = useStyles();
  const { setup, saOris } = useContext(SetupContext);
  const { indexedDb } = useContext(DashboardContext);
  const { user, responseErrorHandler, onlineState, tokenDisabled } =
    useContext(UserContext);
  const inputLabel = useRef(null);
  const [labelWidth, setLabelWidth] = useState(0);
  const { incidentDispatch, vehicleDispatch, incidents } =
    useContext(IncidentsContext);
  const [showVehicleHistoryModal, setShowVehicleHistoryModal] = useState(false);
  const { UPDATE_VIS, UPDATE_VEHICLE, ADD_VIS } = INCIDENT_ACTIONS;
  const { SET_VEHICLE_INFO } = VEHICLE_ACTIONS;
  const client = useApolloClient();
  const {
    updateVisAbandoned,
    updateVisImpounded,
    updateVisOperator,
    updateVisReasonSeized,
    updateVisSuspectDriven,
    updateVisWrecker,
    updateVisTowingDestination,
    updateVisUsedInCrime,
    updateVisVehicleOfInterest,
    updateVisVehicleRole,
    updateVisPosition,
    updateVehicleDescription,
    updateVehicleOwner,
    updateVehiclePlateType,
    updateVehicleValue,
    updateVehicleColor,
    updateVehicleState,
    updateVehicleYear,
    throttledAddVehicleAlert,
    removeVehicleAlert,
    updateVehicleMake,
  } = useContext(MutationContext);

  useEffect(() => {
    if (inputLabel.current) {
      setLabelWidth(inputLabel.current.offsetWidth);
    }
  }, []);

  const stateToMutation = {
    vehicleRole: "vehicleRoleId",
    vehicleMake: "vehicleMakeId",
    vehicleModel: "vehicleModelId",
    year: "year",
    vehiclePlate: "vehiclePlateId",
    vehicleState: "vehicleStateId",
    plateType: "plateTypeId",
    vehicleColor: "vehicleColorId",
    vehicleVin: "vehicleVinId",
    description: "description",
    value: "value",
    reasonSeized: "reasonSeizedId",
    wrecker: "wreckerId",
    towingDestination: "towingDestinationId",
    owner: "ownerId",
    operator: "operatorId",
    abandoned: "abandoned",
    impounded: "impounded",
    suspectDriven: "suspectDriven",
    usedInCrime: "usedInCrime",
    vehicleOfInterest: "vehicleOfInterest",
    position: "position",
  };

  const [incidentPeople, setIncidentPeople] = useState(null);

  useEffect(() => {
    let persons = [];
    piss?.map(pis => {
      if (pis?.person?.id) {
        let person = {
          id: pis?.person?.id,
          name: `${pis?.person?.personalAttribute?.lastName}, ${pis?.person?.personalAttribute?.firstName}`,
        };
        persons.push(person);
      }
      return null;
    });
    setIncidentPeople(persons);
  }, [piss]);

  // Removed vehicleModels, vehicleVins, vehiclePlates from the setupLists
  const {
    setupLists: { states, plateTypes, vehicleColors },
  } = setup;

  const [vehicleMakes, setVehicleMakes] = useState([]);
  const [vehicleModels, setVehicleModels] = useState([]);

  useEffect(() => {
    indexedDb.vehicle_makes
      .toArray()
      .then(res => {
        setVehicleMakes(wordSort("asc", null, res, "name"));
      })
      .catch(err => console.error(err));
  }, [indexedDb.vehicle_makes]);

  useEffect(() => {
    indexedDb.vehicle_models
      .toArray()
      .then(res => {
        setVehicleModels(wordSort("asc", null, res, "name"));
      })
      .catch(err => console.error(err));
  }, [indexedDb.vehicle_models]);

  const localStoreSpecificSetup =
    localGetData(`specific-setup-${incidentOriId}`) || {};
  const { setupLists: specificSetupLists } = localStoreSpecificSetup || {};
  const { vehicleRoles, wreckers, towingDestinations, reasonSeizeds } =
    specificSetupLists || {};

  const handleDispatch = (inIncidentAction, outOfIncidentAction, payload) => {
    if (inIncident) {
      incidentDispatch({ type: inIncidentAction, payload: payload });
    } else {
      vehicleDispatch({ type: outOfIncidentAction, payload: payload });
    }
    return null;
  };

  const handleChange = (event, submit = true) => {
    const name = event.target.name || null;
    //use something like this: Object.hasOwnProperty("id")
    const value = event.target.value;
    const mutationValue = getValueFromEventValue(value);
    const fieldToUpdate = stateToMutation[name];

    if (!name) {
      alert(
        `Please add a name field that matches the state name to the component that was just updated`
      );
      return null;
    }
    let payload = {
      incidentId: incidentId,
      incidentUuid: incidentUuid,
      visId: vis?.id,
      visUuid: vis?.uuid,
      property: name,
      value: value,
    };
    const VIS_ARRAY = [
      "vehicleRole",
      "position",
      "reasonSeized",
      "wrecker",
      "towingDestination",
      "operator",
    ];
    if (VIS_ARRAY.includes(name)) {
      handleDispatch(UPDATE_VIS, SET_VEHICLE_INFO, payload);
    } else {
      payload.vehicleId = vis?.vehicle?.id;
      payload.vehicleUuid = vis?.vehicle?.uuid;
      handleDispatch(UPDATE_VEHICLE, SET_VEHICLE_INFO, payload);
    }
    switch (name) {
      //VIS
      case "vehicleRole":
        submit &&
          updateVisVehicleRole({
            variables: {
              input: {
                originatorId: user?.id,
                incidentId: incidentId ? incidentId : incidentUuid,
                vehicleId: vis?.vehicle?.id
                  ? vis.vehicle.id
                  : vis?.vehicle?.uuid,
                vehicleIncidentSpecificId: vis?.id ? vis.id : vis?.uuid,
                [fieldToUpdate]: mutationValue,
              },
            },
            context: { serializationKey: "incidentQueue" },
          });
        return null;
      case "position":
        submit &&
          updateVisPosition({
            variables: {
              input: {
                originatorId: user?.id,
                incidentId: incidentId ? incidentId : incidentUuid,
                vehicleId: vis?.vehicle?.id
                  ? vis.vehicle.id
                  : vis?.vehicle?.uuid,
                vehicleIncidentSpecificId: vis?.id ? vis.id : vis?.uuid,
                [fieldToUpdate]: +value,
              },
            },
            context: { serializationKey: "incidentQueue" },
          });
        return null;
      case "reasonSeized":
        submit &&
          updateVisReasonSeized({
            variables: {
              input: {
                originatorId: user?.id,
                incidentId: incidentId ? incidentId : incidentUuid,
                vehicleId: vis?.vehicle?.id
                  ? vis.vehicle.id
                  : vis?.vehicle?.uuid,
                vehicleIncidentSpecificId: vis?.id ? vis.id : vis?.uuid,
                [fieldToUpdate]: mutationValue,
              },
            },
            context: { serializationKey: "incidentQueue" },
          });
        return null;
      case "wrecker":
        submit &&
          updateVisWrecker({
            variables: {
              input: {
                originatorId: user?.id,
                incidentId: incidentId ? incidentId : incidentUuid,
                vehicleId: vis?.vehicle?.id
                  ? vis.vehicle.id
                  : vis?.vehicle?.uuid,
                vehicleIncidentSpecificId: vis?.id ? vis.id : vis?.uuid,
                [fieldToUpdate]: mutationValue,
              },
            },
            context: { serializationKey: "incidentQueue" },
          });
        return null;
      case "towingDestination":
        submit &&
          updateVisTowingDestination({
            variables: {
              input: {
                originatorId: user?.id,
                incidentId: incidentId ? incidentId : incidentUuid,
                vehicleId: vis?.vehicle?.id
                  ? vis.vehicle.id
                  : vis?.vehicle?.uuid,
                vehicleIncidentSpecificId: vis?.id ? vis.id : vis?.uuid,
                [fieldToUpdate]: mutationValue,
              },
            },
            context: { serializationKey: "incidentQueue" },
          });
        return null;
      case "owner":
        submit &&
          updateVehicleOwner({
            variables: {
              input: {
                originatorId: user?.id,
                incidentId: incidentId ? incidentId : incidentUuid,
                vehicleIncidentSpecificId: vis.id ? vis.id : vis.uuid,
                id: vis?.vehicle?.id ? vis.vehicle.id : vis?.vehicle?.uuid,
                [fieldToUpdate]: mutationValue,
              },
            },
            context: { serializationKey: "incidentQueue" },
          });
        return null;
      case "operator":
        submit &&
          updateVisOperator({
            variables: {
              input: {
                originatorId: user?.id,
                incidentId: incidentId ? incidentId : incidentUuid,
                vehicleId: vis?.vehicle?.id
                  ? vis.vehicle.id
                  : vis?.vehicle?.uuid,
                vehicleIncidentSpecificId: vis?.id ? vis.id : vis?.uuid,
                [fieldToUpdate]: mutationValue,
              },
            },
            context: { serializationKey: "incidentQueue" },
          });
        return null;

      //VEHICLE
      case "vehiclePlate":
        //needs workflow updated - waiting on backend work
        // submit && updateVehiclePlate({ variables: { input: { originatorId: user?.id, id: vis?.vehicle?.id ? vis.vehicle.id : vis?.vehicle?.uuid, originatorId: user?.id, [fieldToUpdate]: value?.id ? value.id : value } }, context: { serializationKey: 'incidentQueue' } })
        return null;
      case "vehicleVin":
        //needs workflow updated - waiting on backend work
        // submit && updateVehicleVin({ variables: { input: { originatorId: user?.id, id: vis?.vehicle?.id ? vis.vehicle.id : vis?.vehicle?.uuid, originatorId: user?.id, [fieldToUpdate]: value?.id ? value.id : value } }, context: { serializationKey: 'incidentQueue' } })
        return null;
      case "year":
        submit &&
          updateVehicleYear({
            variables: {
              input: {
                originatorId: user?.id,
                incidentId: incidentId ? incidentId : incidentUuid,
                vehicleIncidentSpecificId: vis.id ? vis.id : vis.uuid,
                id: vis?.vehicle?.id ? vis.vehicle.id : vis?.vehicle?.uuid,
                [fieldToUpdate]: mutationValue,
              },
            },
            context: { serializationKey: "incidentQueue" },
          });
        return null;
      case "vehicleState":
        submit &&
          updateVehicleState({
            variables: {
              input: {
                originatorId: user?.id,
                incidentId: incidentId ? incidentId : incidentUuid,
                vehicleIncidentSpecificId: vis.id ? vis.id : vis.uuid,
                id: vis?.vehicle?.id ? vis.vehicle.id : vis?.vehicle?.uuid,
                [fieldToUpdate]: mutationValue,
              },
            },
            context: { serializationKey: "incidentQueue" },
          });
        return null;
      case "plateType":
        submit &&
          updateVehiclePlateType({
            variables: {
              input: {
                originatorId: user?.id,
                incidentId: incidentId ? incidentId : incidentUuid,
                vehicleIncidentSpecificId: vis.id ? vis.id : vis.uuid,
                id: vis?.vehicle?.id ? vis.vehicle.id : vis?.vehicle?.uuid,
                [fieldToUpdate]: mutationValue,
              },
            },
            context: { serializationKey: "incidentQueue" },
          });
        return null;
      case "vehicleColor":
        submit &&
          updateVehicleColor({
            variables: {
              input: {
                originatorId: user?.id,
                incidentId: incidentId ? incidentId : incidentUuid,
                vehicleIncidentSpecificId: vis.id ? vis.id : vis.uuid,
                id: vis?.vehicle?.id ? vis.vehicle.id : vis?.vehicle?.uuid,
                [fieldToUpdate]: mutationValue,
              },
            },
            context: { serializationKey: "incidentQueue" },
          });
        return null;
      case "description":
        submit &&
          updateVehicleDescription({
            variables: {
              input: {
                originatorId: user?.id,
                incidentId: incidentId ? incidentId : incidentUuid,
                vehicleIncidentSpecificId: vis.id ? vis.id : vis.uuid,
                id: vis?.vehicle?.id ? vis.vehicle.id : vis?.vehicle?.uuid,
                [fieldToUpdate]: mutationValue,
              },
            },
            context: { serializationKey: "incidentQueue" },
          });
        return null;

      case "value":
        submit &&
          updateVehicleValue({
            variables: {
              input: {
                originatorId: user?.id,
                incidentId: incidentId ? incidentId : incidentUuid,
                vehicleIncidentSpecificId: vis.id ? vis.id : vis.uuid,
                id: vis?.vehicle?.id ? vis.vehicle.id : vis?.vehicle?.uuid,
                [fieldToUpdate]: mutationValue,
              },
            },
            context: { serializationKey: "incidentQueue" },
          });
        return null;
      default:
        alert(
          `Name not found in handleChange function in VehicleDetail.js: ${name}`
        );
        return null;
    }
  };

  const handleCheckbox = e => {
    const value = e.target.checked;
    const name = e.target.value;
    const fieldToUpdate = stateToMutation[name];

    if (!name) {
      alert(
        `Please add a name field that matches the state name to the component that was just updated`
      );
      return null;
    }
    switch (name) {
      case "abandoned":
        incidentDispatch({
          type: UPDATE_VIS,
          payload: {
            incidentId: incidentId,
            incidentUuid: incidentUuid,
            visId: vis?.id,
            visUuid: vis?.uuid,
            property: name,
            value: value,
          },
        });
        updateVisAbandoned({
          variables: {
            input: {
              originatorId: user?.id,
              incidentId: incidentId ? incidentId : incidentUuid,
              vehicleId: vis?.vehicle?.id ? vis.vehicle.id : vis?.vehicle?.uuid,
              vehicleIncidentSpecificId: vis?.id ? vis.id : vis?.uuid,
              [fieldToUpdate]: value,
            },
          },
          context: { serializationKey: "incidentQueue" },
        });
        return null;

      case "impounded":
        incidentDispatch({
          type: UPDATE_VIS,
          payload: {
            incidentId: incidentId,
            incidentUuid: incidentUuid,
            visId: vis?.id,
            visUuid: vis?.uuid,
            property: name,
            value: value,
          },
        });
        updateVisImpounded({
          variables: {
            input: {
              originatorId: user?.id,
              incidentId: incidentId ? incidentId : incidentUuid,
              vehicleId: vis?.vehicle?.id ? vis.vehicle.id : vis?.vehicle?.uuid,
              vehicleIncidentSpecificId: vis?.id ? vis.id : vis?.uuid,
              [fieldToUpdate]: value,
            },
          },
          context: { serializationKey: "incidentQueue" },
        });
        return null;

      case "suspectDriven":
        incidentDispatch({
          type: UPDATE_VIS,
          payload: {
            incidentId: incidentId,
            incidentUuid: incidentUuid,
            visId: vis?.id,
            visUuid: vis?.uuid,
            property: name,
            value: value,
          },
        });
        updateVisSuspectDriven({
          variables: {
            input: {
              originatorId: user?.id,
              incidentId: incidentId ? incidentId : incidentUuid,
              vehicleId: vis?.vehicle?.id ? vis.vehicle.id : vis?.vehicle?.uuid,
              vehicleIncidentSpecificId: vis?.id ? vis.id : vis?.uuid,
              [fieldToUpdate]: value,
            },
          },
          context: { serializationKey: "incidentQueue" },
        });
        return null;

      case "usedInCrime":
        incidentDispatch({
          type: UPDATE_VIS,
          payload: {
            incidentId: incidentId,
            incidentUuid: incidentUuid,
            visId: vis?.id,
            visUuid: vis?.uuid,
            property: name,
            value: value,
          },
        });
        updateVisUsedInCrime({
          variables: {
            input: {
              originatorId: user?.id,
              incidentId: incidentId ? incidentId : incidentUuid,
              vehicleId: vis?.vehicle?.id ? vis.vehicle.id : vis?.vehicle?.uuid,
              vehicleIncidentSpecificId: vis?.id ? vis.id : vis?.uuid,
              [fieldToUpdate]: value,
            },
          },
          context: { serializationKey: "incidentQueue" },
        });
        return null;

      case "vehicleOfInterest":
        incidentDispatch({
          type: UPDATE_VIS,
          payload: {
            incidentId: incidentId,
            incidentUuid: incidentUuid,
            visId: vis?.id,
            visUuid: vis?.uuid,
            property: name,
            value: value,
          },
        });
        updateVisVehicleOfInterest({
          variables: {
            input: {
              originatorId: user?.id,
              incidentId: incidentId ? incidentId : incidentUuid,
              vehicleId: vis?.vehicle?.id ? vis.vehicle.id : vis?.vehicle?.uuid,
              vehicleIncidentSpecificId: vis?.id ? vis.id : vis?.uuid,
              [fieldToUpdate]: value,
            },
          },
          context: { serializationKey: "incidentQueue" },
        });
        return null;

      default:
        alert(
          `Name not found in handlevChange function in VehicleDetail.js: ${name}`
        );
        return null;
    }
  };

  const handleRemoveVehicle = useHandleRemoveVehicle(
    incidents[incidentIndex],
    incidentIndex,
    client
  );

  const handleRemoveClick = async () => {
    try {
      const { success, cancelled, errors } = await handleRemoveVehicle(vis, visIndex);
      if (cancelled) return;
      if (errors.length > 0) {
        alert(errors.join("\n\n"));
      } else if (success) {
        setVehicleTab(vehicleTab > 0 ? vehicleTab - 1 : 0);
      } else {
        alert("An error occurred while removing the vehicle.");
      }
    } catch (error) {
      console.error(error);
      alert("An error occurred while removing the vehicle in catch.");
    }
  };

  const [addVisToIncident] = useMutation(ADD_OR_UPDATE_VEHICLE_IN_INCIDENT, {
    context: { serializationKey: "incidentQueue" },
    onCompleted: data => {
      if (data) {
        responseErrorHandler(data);
        const vis = data.addOrUpdateVehicleInIncident.vehicleIncidentSpecific;
        const visUuid =
          data.addOrUpdateVehicleInIncident.vehicleIncidentSpecificUuid;
        const vehicleUuid = data.addOrUpdateVehicleInIncident.vehicleUuid;

        //update the visId to be the non-uuid id
        incidentDispatch({
          type: UPDATE_VIS,
          payload: {
            incidentId: incidentId,
            incidentUuid: incidentUuid,
            visUuid: visUuid,
            property: "id",
            value: vis.id,
          },
        });
        //update the vehicleId to be the non-uuid id
        incidentDispatch({
          type: UPDATE_VEHICLE,
          payload: {
            incidentId: incidentId,
            incidentUuid: incidentUuid,
            vehicleUuid: vehicleUuid,
            property: "id",
            value: vis.vehicle.id,
          },
        });
      }
    },
  });

  const handleAddVisToIncident = () => {
    const localVis = createEmptyVis();
    incidentDispatch({
      type: ADD_VIS,
      payload: { incidentIndex: incidentIndex, vis: localVis },
    });
    addVisToIncident({
      variables: {
        input: {
          originatorId: user?.id,
          incidentId: incidentId ? incidentId : incidentUuid,
          vehicleId: localVis.vehicle.id,
          vehicleIncidentSpecificId: localVis.id,
        },
      },
      context: { serializationKey: "incidentQueue" },
    });
    setVehicleTab(vissLength);
  };

  const handleAddVehicleAlert = alert => {
    let localAlerts = [...(vis?.vehicle?.validVehicleAlerts ?? [])];
    localAlerts.push(alert);
    incidentDispatch({
      type: UPDATE_VEHICLE,
      payload: {
        incidentIndex: incidentIndex,
        visIndex: visIndex,
        property: "validVehicleAlerts",
        value: localAlerts,
      },
    });
    throttledAddVehicleAlert({
      variables: {
        input: {
          vehicleId: vis?.vehicle?.id,
          officerId: user?.id,
          originatorId: user?.id,
          vehicleAlertId: alert.id,
          alertTypeId: alert?.alertType?.id,
          notes: alert?.notes,
          expireAt: alert?.expireAt,
          incidentId: incidentId ? incidentId : null,
        },
      },
      context: {
        serializationKey: "incidentQueue",
      },
    }).then(response => {
      if (response?.data?.addVehicleAlert) {
        if (
          response?.data?.addVehicleAlert?.vehicleAlertUuid.includes("Offline_")
        ) {
          let alertArrWithId = JSON.parse(JSON.stringify(localAlerts));
          alertArrWithId.map(la => {
            if (la.id === response?.data?.addVehicleAlert?.vehicleAlertUuid) {
              la.id = response?.data?.addVehicleAlert?.vehicleAlert.id;
            }
            return la;
          });
          incidentDispatch({
            type: UPDATE_VEHICLE,
            payload: {
              incidentIndex: incidentIndex,
              visIndex: visIndex,
              property: "validVehicleAlerts",
              value: alertArrWithId,
            },
          });
        }
      }
    });
  };

  const handleRemoveVehicleAlert = alertId => {
    removeVehicleAlert({
      variables: { input: { vehicleAlertId: alertId, originatorId: user?.id } },
      context: {
        serializationKey: "incidentQueue",
      },
    });
    let localAlerts = vis?.vehicle?.validVehicleAlerts.filter(
      vpa => vpa.id.toString() !== alertId.toString()
    );
    incidentDispatch({
      type: UPDATE_VEHICLE,
      payload: {
        incidentIndex: incidentIndex,
        visIndex: visIndex,
        property: "validVehicleAlerts",
        value: localAlerts,
      },
    });
  };

  const [addVehicleToNewIncident] = useMutation(
    ADD_OR_UPDATE_VEHICLE_IN_INCIDENT,
    {
      context: { serializationKey: "incidentQueue" },
      onCompleted: data => {
        if (data) {
          responseErrorHandler(data);
        }
      },
    }
  );

  const startIncidentWithVehicle = vehicleId => {
    const incidentPayload = createEmptyIncident(user, false, saOris);
    const input = {
      officerId: user?.id,
      oriId: user?.homeOriId,
      uuid: incidentPayload.id,
      callTime: incidentPayload.callTime,
    };
    //this is throttled in the parent component
    createNewIncident({
      variables: { input },
      context: { serializationKey: "incidentQueue" },
    }).then(res => {
      const incidentId = res?.data?.newIncident?.incidentId;
      addVehicleToNewIncident({
        variables: {
          input: {
            incidentId: incidentId,
            originatorId: user?.id,
            vehicleId: vehicleId,
          },
        },
        context: {
          serializationKey: "incidentQueue",
        },
      });
    });
  };

  const handleAutoCompleteChange = (value, name) => {
    const fieldToUpdate = stateToMutation[name];
    let input = {
      id: vis?.vehicle?.id,
      originatorId: user?.id,
      vehicleIncidentSpecificId: vis?.id,
      incidentId: incidentId,
    };
    if (value?.action === "Add") {
      if (
        window.confirm(
          `Are you sure you want to add a non-validated ${
            name === "vehicleMake" ? "make" : "model"
          }?`
        )
      ) {
        const localLookup = {
          vehicleMake: "vehicleMakeName",
          vehicleModel: "vehicleModelName",
        };
        const makeOrModelName = localLookup[name];
        input[makeOrModelName] = value?.name;
      }
    } else {
      input[fieldToUpdate] = value?.id ? value.id : "";
    }
    // switch (name) {
    if (name === "vehicleMake" && value === null) {
      input.vehicleModelId = "";
      incidentDispatch({
        type: UPDATE_VEHICLE,
        payload: {
          incidentIndex: incidentIndex,
          visIndex: visIndex,
          property: "vehicleModel",
          value: "",
        },
      });
    }
    // case "vehicleMake":
    incidentDispatch({
      type: UPDATE_VEHICLE,
      payload: {
        incidentIndex: incidentIndex,
        visIndex: visIndex,
        property: name,
        value: value,
      },
    });
    updateVehicleMake({ variables: { input: input } });

    return null;
    //   case "vehicleModel":
    //     incidentDispatch({ type: UPDATE_VEHICLE, payload: { incidentIndex: incidentIndex, visIndex: visIndex, property: 'vehicleMake', value: value } })
    //     updateVehicleMake({ variables: { input: input } })
    //     return null;

    //   default:
    //     alert('Name not found in handleAutoCompleteChange function in VehicleDetail.js')
    //     return null;
    // }
  };

  if (incidentPeople) {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          flexWrap: "wrap",
          maxWidth: "100%",
          justifyContent: "flex-start",
          border: "1px solid black",
          alignItems: "flex-end",
        }}
      >
        <SingleDropDown
          handleChange={handleChange}
          stateName={"vehicleRole"}
          value={vis?.vehicleRole}
          options={vehicleRoles}
          title={"Role"}
          classStyle={classes.formControl}
          inputLabel={inputLabel}
          labelWidth={labelWidth}
          userRoles={userRoles}
          {...props}
        />
        <span
          style={{
            display: "flex",
            flexDirection: "row",
            flexWrap: "no-wrap",
            alignItems: "flex-end",
          }}
        >
          <VehicleAutoSuggest
            title={"Vehicle Plate"}
            vehicle={vis?.vehicle || {}}
            incidentId={incidentId}
            incidentIndex={incidentIndex}
            visId={vis?.id ? vis?.id : vis?.uuid}
            visIndex={visIndex}
            type={"plate"}
            userRoles={userRoles}
            {...props}
          />
          <TwentyEightButton
            vehicle={vis?.vehicle}
            user={user}
            incidentId={incidentId}
            vinOnly={false}
            plateOnly={true}
            setPopper={setPopper}
            tokenDisabled={tokenDisabled}
            incidentOriId={incidentOriId}
          />
          <AlertComponent
            parentObj={vis?.vehicle}
            accessor={"validVehicleAlerts"}
            type={"vehicle"}
            updateHandler={handleAddVehicleAlert}
            deleteHandler={handleRemoveVehicleAlert}
            userRoles={userRoles}
            incidentId={incidentId}
            {...props}
          />
        </span>
        <span
          style={{
            display: "flex",
            flexDirection: "row",
            flexWrap: "no-wrap",
            alignItems: "flex-end",
          }}
        >
          <VehicleAutoSuggest
            title={"VIN"}
            vehicle={vis?.vehicle || {}}
            incidentId={incidentId}
            incidentIndex={incidentIndex}
            visId={vis?.id ? vis?.id : vis?.uuid}
            visIndex={visIndex}
            type={"vin"}
            userRoles={userRoles}
            {...props}
          />
          <TwentyEightButton
            vehicle={vis?.vehicle}
            user={user}
            incidentId={incidentId}
            vinOnly={true}
            plateOnly={false}
            setPopper={setPopper}
            tokenDisabled={tokenDisabled}
            incidentOriId={incidentOriId}
          />
        </span>
        <span>
          <CustomAutoComplete
            autoSelect={false}
            matchFromStart={true}
            title={"Vehicle\xa0Make"}
            options={
              vis?.vehicle?.vehicleModel
                ? vehicleMakes.filter(
                    make =>
                      make.id === vis?.vehicle?.vehicleModel?.vehicleMakeId
                  )
                : vehicleMakes
            }
            addMargin={true}
            value={vis?.vehicle?.vehicleMake ? vis?.vehicle?.vehicleMake : null}
            stateName={"vehicleMake"}
            updateFields={handleAutoCompleteChange}
            renderOption={option => {
              if (option.action === "Add") {
                return option.value;
              } else {
                return option.name;
              }
            }}
            minWidth={"350px"}
            userRoles={user?.roleNames}
            allowAdd={true}
            {...props}
          />
          {vis?.vehicle?.vehicleMake && !vis?.vehicle?.vehicleMake?.nhtsaId && (
            <FormHelperText
              className={clsx(classes.leftMarginMatch, classes.redLabel)}
            >
              The make has not been validated by the NHTSA.
            </FormHelperText>
          )}
        </span>
        <span>
          <CustomAutoComplete
            autoSelect={false}
            matchFromStart={true}
            title={"Vehicle\xa0Model"}
            // options={vis?.vehicle?.vehicleMake?.nhtsaId ? _.uniqBy(vehicleMakes.find(make => make.id === vis?.vehicle?.vehicleMake?.id)?.vehicleModels, (model) => model?.nhtsaId)  : vehicleModels}
            options={
              vis?.vehicle?.vehicleMake?.nhtsaId
                ? _.uniqBy(
                    vehicleModels.filter(
                      model =>
                        model.vehicleMakeId === vis?.vehicle?.vehicleMake?.id
                    ),
                    model => model?.nhtsaId
                  )
                : vehicleModels
            }
            // options={vehicleModels}
            addMargin={true}
            value={
              vis?.vehicle?.vehicleModel ? vis?.vehicle?.vehicleModel : null
            }
            stateName={"vehicleModel"}
            updateFields={handleAutoCompleteChange}
            renderOption={option => {
              if (option.action === "Add") {
                return option.value;
              } else {
                return option.name;
              }
            }}
            allowAdd={true}
            minWidth={"350px"}
            userRoles={user?.roleNames}
            {...props}
          />
          {vis?.vehicle?.vehicleModel &&
            !vis?.vehicle?.vehicleModel?.nhtsaId && (
              <FormHelperText
                className={clsx(classes.leftMarginMatch, classes.redLabel)}
              >
                The model has not been validated by the NHTSA.
              </FormHelperText>
            )}
        </span>
        <TextField
          data-cy="year"
          inputProps={{ "data-cy": "year-input" }}
          InputLabelProps={{ shrink: true }}
          id="year-id"
          disabled={memoizedLocked}
          label="Vehicle Year"
          value={vis?.vehicle?.year || ""}
          name={"year"}
          onChange={e => handleChange(e, false)}
          onBlur={e => handleChange(e, true)}
          onKeyDown={e => {
            if (e.key === "Enter") {
              e.target.blur();
            }
          }}
          className={classes.formControl}
        />
        <SingleDropDown
          handleChange={handleChange}
          stateName={"vehicleState"}
          value={vis?.vehicle?.vehicleState}
          options={states}
          title={"State"}
          classStyle={classes.formControl}
          inputLabel={inputLabel}
          labelWidth={labelWidth}
          userRoles={userRoles}
          {...props}
        />
        <SingleDropDown
          handleChange={handleChange}
          stateName={"plateType"}
          value={vis?.vehicle?.plateType}
          options={plateTypes}
          title={"Plate Type"}
          classStyle={classes.formControl}
          inputLabel={inputLabel}
          labelWidth={labelWidth}
          userRoles={userRoles}
          {...props}
        />
        <SingleDropDown
          handleChange={handleChange}
          stateName={"vehicleColor"}
          value={vis?.vehicle?.vehicleColor}
          options={vehicleColors}
          title={"Color"}
          classStyle={classes.formControl}
          inputLabel={inputLabel}
          labelWidth={labelWidth}
          userRoles={userRoles}
          {...props}
        />
        <TextField
          data-cy="description"
          inputProps={{ "data-cy": "description-input" }}
          InputLabelProps={{ shrink: true }}
          id="description-id"
          label="Vehicle Description"
          value={vis?.vehicle?.description || ""}
          name={"description"}
          disabled={memoizedLocked}
          onChange={e => handleChange(e, false)}
          onBlur={e => handleChange(e, true)}
          onKeyDown={e => {
            if (e.key === "Enter") {
              e.target.blur();
            }
          }}
          className={classes.formControl}
        />
        <TextField
          data-cy="value"
          inputProps={{ "data-cy": "value-input" }}
          InputLabelProps={{ shrink: true }}
          id="value-id"
          label="Value"
          disabled={memoizedLocked}
          value={vis?.vehicle?.value || ""}
          name={"value"}
          onChange={e => handleChange(e, false)}
          onBlur={e => handleChange(e, true)}
          onKeyDown={e => {
            if (e.key === "Enter") {
              e.target.blur();
            }
          }}
          type={"number"}
          className={classes.formControl}
          InputProps={{
            startAdornment: <InputAdornment position="start">$</InputAdornment>,
          }}
        />
        <SingleDropDown
          handleChange={handleChange}
          stateName={"reasonSeized"}
          value={vis?.reasonSeized}
          options={reasonSeizeds}
          title={"Reason Seized/Towed"}
          classStyle={classes.formControl}
          inputLabel={inputLabel}
          labelWidth={labelWidth}
          userRoles={userRoles}
          {...props}
        />
        <SingleDropDown
          handleChange={handleChange}
          stateName={"wrecker"}
          value={vis?.wrecker}
          options={wreckers}
          title={"Towed By"}
          classStyle={classes.formControl}
          inputLabel={inputLabel}
          labelWidth={labelWidth}
          userRoles={userRoles}
          {...props}
        />
        <SingleDropDown
          handleChange={handleChange}
          stateName={"towingDestination"}
          value={vis?.towingDestination}
          options={towingDestinations}
          title={"Towed To"}
          classStyle={classes.formControl}
          inputLabel={inputLabel}
          labelWidth={labelWidth}
          userRoles={userRoles}
          {...props}
        />
        <SingleDropDown
          handleChange={handleChange}
          stateName={"owner"}
          value={
            incidentPeople.length > 0
              ? incidentPeople.find(
                  person =>
                    person.id.toString() === vis?.vehicle?.owner?.id?.toString()
                )
              : {}
          }
          options={incidentPeople}
          title={"Owner"}
          classStyle={classes.formControl}
          inputLabel={inputLabel}
          labelWidth={labelWidth}
          userRoles={userRoles}
          {...props}
        />
        <SingleDropDown
          handleChange={handleChange}
          stateName={"operator"}
          value={
            incidentPeople.length > 0
              ? incidentPeople.find(
                  person => person.id === vis?.operator?.id?.toString()
                )
              : {}
          }
          options={incidentPeople}
          title={"Operator"}
          classStyle={classes.formControl}
          inputLabel={inputLabel}
          labelWidth={labelWidth}
          userRoles={userRoles}
          {...props}
        />
        <TextField
          data-cy="position"
          inputProps={{ "data-cy": "position-input" }}
          InputLabelProps={{ shrink: true }}
          id="position-id"
          label="Position"
          value={vis?.position || ""}
          name={"position"}
          onChange={e => handleChange(e, false)}
          onBlur={e => handleChange(e, true)}
          onKeyDown={e => {
            if (e.key === "Enter") {
              e.target.blur();
            }
          }}
          className={classes.formControl}
          type={"number"}
          disabled={memoizedLocked}
        />
        <Box display="flex" flexDirection="row" style={{ width: "100%" }}>
          <TextField
            data-cy="evidence-status"
            inputProps={{ "data-cy": "evidence-status-input" }}
            InputLabelProps={{ shrink: true }}
            id="evidence-status-id"
            label="Evidence Status"
            value={vis?.evidenceStatus || ""}
            className={classes.formControl}
            disabled={true}
            // style={{width: '250px'}}
            style={{ flexGrow: 1, maxWidth: "300px" }}
          />
          <TextField
            data-cy="recovered-value"
            inputProps={{ "data-cy": "recovered-value-input" }}
            InputLabelProps={{ shrink: true }}
            id="recovered-value-id"
            label="Recovered Value"
            value={vis?.recoveredValue || ""}
            className={classes.formControl}
            disabled={true}
            style={{ flexGrow: 1, maxWidth: "300px" }}
          />
          <TextField
            data-cy="date-stolen"
            inputProps={{ "data-cy": "date-stolen-input" }}
            InputLabelProps={{ shrink: true }}
            id="date-stolen-id"
            label="Date stolen"
            value={
              vis?.dateStolen
                ? moment
                    .utc(vis?.dateStolen, "YYYY-MM-DD HH:mm:ss UTC")
                    .local()
                    .format("MM/DD/YY")
                : ""
            }
            className={classes.formControl}
            disabled={true}
            style={{ flexGrow: 1, maxWidth: "300px" }}
          />
          <TextField
            data-cy="date-recovered"
            inputProps={{ "data-cy": "date-recovered-input" }}
            InputLabelProps={{ shrink: true }}
            id="date-recovered-id"
            label="Date recovered"
            value={
              vis?.dateRecovered
                ? moment
                    .utc(vis?.dateRecovered, "YYYY-MM-DD HH:mm:ss UTC")
                    .local()
                    .format("MM/DD/YY")
                : ""
            }
            className={classes.formControl}
            disabled={true}
            style={{ flexGrow: 1, maxWidth: "300px" }}
          />
        </Box>
        <Box
          display="flex"
          flexDirection="row"
          justifyContent={"space-between"}
          style={{ width: "100%" }}
        >
          <CheckboxWithLabel
            userRoles={userRoles}
            {...props}
            checked={vis?.abandoned}
            onChange={handleCheckbox}
            stateName={"abandoned"}
            title={"Abandoned"}
          />
          <CheckboxWithLabel
            userRoles={userRoles}
            {...props}
            checked={vis?.impounded}
            onChange={handleCheckbox}
            stateName={"impounded"}
            title={"Impounded"}
          />
          <CheckboxWithLabel
            userRoles={userRoles}
            {...props}
            checked={vis?.suspectDriven}
            onChange={handleCheckbox}
            stateName={"suspectDriven"}
            title={"Driven By Suspect"}
          />
          <CheckboxWithLabel
            userRoles={userRoles}
            {...props}
            checked={vis?.usedInCrime}
            onChange={handleCheckbox}
            stateName={"usedInCrime"}
            title={"Used In Crime"}
          />
          <CheckboxWithLabel
            userRoles={userRoles}
            {...props}
            checked={vis?.vehicleOfInterest}
            onChange={handleCheckbox}
            stateName={"vehicleOfInterest"}
            title={"Vehicle of Interest"}
          />
        </Box>
        <Box
          display="flex"
          flexDirection="row"
          justifyContent="space-around"
          style={{ width: "100%" }}
        >
          {inIncident && (
            <Button
              style={{
                width: "fit-content",
                marginTop: "1vh",
                marginBottom: "3vh",
                marginRight: "2vw",
              }}
              disabled={memoizedLocked}
              variant="contained"
              onClick={handleRemoveClick}
            >
              Delete Vehicle
            </Button>
          )}
          {inIncident && (
            <Button
              style={{
                width: "fit-content",
                marginTop: "1vh",
                marginBottom: "3vh",
                marginRight: "2vw",
              }}
              disabled={memoizedLocked || isVisEmpty(vis)}
              variant="contained"
              onClick={handleAddVisToIncident}
            >
              Add Vehicle
            </Button>
          )}
          {inIncident && (
            <Button
              style={{
                width: "fit-content",
                marginTop: "1vh",
                marginBottom: "3vh",
                marginRight: "2vw",
              }}
              disabled={memoizedLocked || !onlineState}
              variant="contained"
              onClick={() => setShowVehicleSearch(true)}
            >
              Search
            </Button>
          )}
          {/* <Button style={{ width: "fit-content", marginTop: '1vh', marginBottom: '3vh', marginRight: "2vw" }} variant="contained" onClick={() => alert('Do something here')}>Print</Button> */}
          {/* <Button disabled={!onlineState && import.meta.env.VITE_FORCE_ONLINE !== 'true'} style={{ width: "fit-content", marginTop: '1vh', marginBottom: '3vh', marginRight: "2vw" }} variant="contained" onClick={() => alert('Do something here')}>Search</Button> */}
          {/* <Button disabled={!onlineState && import.meta.env.VITE_FORCE_ONLINE !== 'true'} style={{ width: "fit-content", marginTop: '1vh', marginBottom: '3vh', marginRight: "2vw" }} variant="contained" onClick={() => alert('Do something here')}>Send to Crash</Button> */}
          <Button
            disabled={
              !onlineState && import.meta.env.VITE_FORCE_ONLINE !== "true"
            }
            style={{
              width: "fit-content",
              marginTop: "1vh",
              marginBottom: "3vh",
              marginRight: "2vw",
            }}
            variant="contained"
            onClick={() => setShowVehicleHistoryModal(true)}
          >
            Vehicle History
          </Button>
          {!inIncident && (
            <Button
              disabled={
                !onlineState && import.meta.env.VITE_FORCE_ONLINE !== "true"
              }
              style={{
                width: "fit-content",
                marginTop: "1vh",
                marginBottom: "3vh",
                marginRight: "2vw",
              }}
              variant="contained"
              onClick={() => startIncidentWithVehicle(vis?.vehicle?.id)}
            >
              Start Incident
            </Button>
          )}
        </Box>

        <VehicleHistoryModal
          modalOpen={showVehicleHistoryModal}
          setModalOpen={setShowVehicleHistoryModal}
          vehicle={vis?.vehicle}
          userRoles={user?.userRoles}
          user={user}
        />
      </div>
    );
  } else {
    return (
      <div>
        <CircularProgress data-cy="loading-circle" />
      </div>
    );
  }
};

VehicleDetail.propTypes = {
  showVehicleSearch: PropTypes.bool,
  setShowVehicleSearch: PropTypes.func,
  vis: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    abandoned: PropTypes.bool,
    impounded: PropTypes.bool,
    operator: PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      personalAttribute: PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        firstName: PropTypes.string,
        lastName: PropTypes.string,
      }),
    }),
    owner: PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      personalAttribute: PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        firstName: PropTypes.string,
        lastName: PropTypes.string,
      }),
    }),
    reasonSeized: PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      name: PropTypes.string,
    }),
    suspectDriven: PropTypes.bool,
    wrecker: PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      name: PropTypes.string,
    }),
    towingDestination: PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      name: PropTypes.string,
    }),
    usedInCrime: PropTypes.bool,
    vehicle: PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      description: PropTypes.string,
      plateType: PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        name: PropTypes.string,
      }),
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      vehicleColor: PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        name: PropTypes.string,
      }),
      vehicleMake: PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        name: PropTypes.string,
      }),
      vehicleModel: PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        name: PropTypes.string,
      }),
      vehiclePlate: PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        name: PropTypes.string,
      }),
      vehicleState: PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        name: PropTypes.string,
      }),
      vehicleVin: PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        name: PropTypes.string,
      }),
      year: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }),
    vehicleOfInterest: PropTypes.bool,
    vehicleRole: PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      name: PropTypes.string,
    }),
    position: PropTypes.string,
    vehiclePlate: PropTypes.string,
  }),
  visIndex: PropTypes.number,
  incidentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  incidentUuid: PropTypes.string,
  incidentIndex: PropTypes.number,
  piss: PropTypes.array,
  vissLength: PropTypes.number,
  vehicleTab: PropTypes.number,
  setVehicleTab: PropTypes.func,
  incidentOriId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  disabled: PropTypes.bool,
  userRoles: PropTypes.array,
  inIncident: PropTypes.bool,
  createNewIncident: PropTypes.func,
  setPopper: PropTypes.func,
  canEdit: PropTypes.bool,
};

export default VehicleDetail;
